<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate, useParams } from 'svelte-navigator';
  import {
    Alert,
    Breadcrumb,
    BreadcrumbItem,
    Button,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Offcanvas,
    Row,
    Tooltip
  } from '@sveltestrap/sveltestrap';

  import { MultiSelectField, SwitchField, TextField } from '@client/components/fields/standalone';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { SignatureEditor } from '@client/components/signature-editor';
  import { SignatureFieldsList } from '@client/components/signature-fields-list';
  import { SignaturePreviewSettings } from '@client/components/signature-preview-settings';

  import { DomainsService } from '@client/services/domains';
  import { GroupsService } from '@client/services/groups';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';
  import { UsersService } from '@client/services/users';

  import { appStore } from '@client/stores/app';
  import { isOutlookVisualPreview } from '@client/stores/domain-settings';
  import { buildDraftSignature } from '@client/stores/signature-templates';
  import { userStore } from '@client/stores/user';

  import { formatHTML } from '@client/utils/html-formatter';
  import { generateUniqueRulename } from '@client/utils/rulenames';
  import { sanitizeInputValue } from '@client/utils/sanitize';
  import {
    extractActiveParams,
    extractInternalParams,
    extractSpecialParams,
    fixImagesInline,
    getAllSignatureWarnings,
    getSignatureWarnings
  } from '@client/utils/signatures';
  import { userMetadata } from '@client/utils/users';

  import { FIELD_TYPES, SIGNATURE_WARNINGS, USER_REQUEST_PARAMS } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';
  import { deepCopy } from '@common/utils/objects';

  import { CloneModal } from './modals';
  import SignatureParamWarnings from './SignatureParamWarnings.svelte';

  export let create = false;

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  const params = useParams();
  const navigate = useNavigate();

  const redirect = (path) => navigate(path);

  // Cap how many issue items a warning tooltip lists before collapsing the rest into "+ N more".
  const MAX_TOOLTIP_ITEMS = 5;

  let originalResult = {};
  let queryResult = null;
  let users = [];
  let originalUsers = [];
  let loaded = false;
  let hasRouting = false;
  let updating = false;
  let isSalesforce = false;
  let primaryWarnings = [];
  let primaryTextWarnings = [];
  let replyWarnings = [];
  let replyTextWarnings = [];
  let groupOptions = [];

  let activeFields = [];
  let activeInternalFields = [];
  let activeReplyFields = [];
  let activeReplyInternalFields = [];
  let activeSpecialFields = {};
  let activeTextFields = [];
  let activeReplyTextFields = [];

  let actions = [];
  let fields = [];

  let showCloneToDomainModal = false;
  let cloneSignature = {};
  let cloneSignatureRulename = '';
  let domains = [];

  let currentElement = null;
  let rightPaneElement = null;
  let offcanvasElement = null;
  let showEditorPane = false;
  let errorMessage = '';
  let previewSettingsComponent;

  let showSignatureSource = false;
  let showReplySignatureSource = false;
  let showTextSignatureSource = false;

  let signatureName = $params.id;

  let selectedUser = {
    label: '',
    value: ''
  };

  // Signatures are usually read by people outside the domain, so preview external by default. This is
  // the address mailapi falls back to for an empty recipient; sending it explicitly keeps it visible.
  let recipientEmail = 'test@recipient.com';

  const onCloneSignatureToDomain = () => {
    cloneSignature = queryResult;
    cloneSignatureRulename = queryResult.rulename;
    showCloneToDomainModal = true;
  };

  const onUpdate = (id, label, value) => {
    queryResult[id] = value;
    queryResult = { ...queryResult };
  };

  const addToSignatureFields = (name, type) => {
    fields = [...fields, { name, hide: false, transform: false }];
    queryResult.params = [...fields];
    updateFieldWarnings();
  };

  const fixInlineImages = (template) => {
    queryResult[template] = fixImagesInline(queryResult[template]);
    queryResult = { ...queryResult };
    updateFieldWarnings();
  };

  const updateFieldWarnings = () => {
    const query = { ...queryResult, params: deepCopyArray(fields) };
    const warningOptions = { isOutlookVisualPreview: $isOutlookVisualPreview };

    primaryWarnings = getSignatureWarnings(query, '', [], 'htmltemplate', 'params', warningOptions);
    replyWarnings = getSignatureWarnings(query, '', [], 'replyhtmltemplate', 'params', warningOptions);
    primaryTextWarnings = getSignatureWarnings(query, '', [], 'texttemplate', 'params', warningOptions);
    replyTextWarnings = getSignatureWarnings(query, '', [], 'replytexttemplate', 'params', warningOptions);
  };

  const onFieldsChange = (event) => {
    fields = event.detail.fields;
    queryResult.params = [...fields];
    updateFieldWarnings();
  };

  const onResetFields = () => {
    fields = [...deepCopy(originalResult).params];
    queryResult.params = [...fields];
    updateFieldWarnings();
  };

  const onError = ({ message, text, notifyOnly }) => {
    addNotification({
      text,
      type: 'error',
      autohide: 2500,
      position: 'top-right'
    });

    if (!notifyOnly) {
      errorMessage = message;
    }
  };

  const updateSignatureConfig = async () => {
    queryResult.params.forEach((field) => {
      field.transform = field.transform || false;
    });

    try {
      queryResult.replyhtmltemplate = await formatHTML(queryResult.replyhtmltemplate);
    } catch {
      console.log('Error formatting HTML template');
    }

    try {
      queryResult.htmltemplate = await formatHTML(queryResult.htmltemplate);
    } catch {
      console.log('Error formatting HTML template');
    }

    originalResult = deepCopy(queryResult);

    signatureName = queryResult.rulename;

    fields = [...queryResult.params];

    const getActiveParams = extractActiveParams(queryResult.htmltemplate);
    const getActiveTextParams = extractActiveParams(queryResult.texttemplate);
    const sortedFields = fields.slice().sort((a, b) => a.name.localeCompare(b.name));

    activeInternalFields = extractInternalParams(queryResult.htmltemplate);
    activeFields = sortedFields.map(({ name }) => getActiveParams.includes(name) && name).filter(Boolean);
    activeTextFields = sortedFields.map(({ name }) => getActiveTextParams.includes(name) && name).filter(Boolean);
    activeSpecialFields = extractSpecialParams(queryResult.htmltemplate);

    if (queryResult.replytemplateenabled) {
      const getActiveReplyParams = extractActiveParams(queryResult.replyhtmltemplate);
      activeReplyFields = sortedFields.map(({ name }) => getActiveReplyParams.includes(name) && name).filter(Boolean);
      activeReplyInternalFields = extractInternalParams(queryResult.replyhtmltemplate);

      if (queryResult.replytexttemplate) {
        const getActiveReplyTextParams = extractActiveParams(queryResult.replytexttemplate);
        activeReplyTextFields = sortedFields
          .map(({ name }) => getActiveReplyTextParams.includes(name) && name)
          .filter(Boolean);
      }
    }

    const warningOptions = { isOutlookVisualPreview: $isOutlookVisualPreview };

    primaryWarnings = getSignatureWarnings(queryResult, '', [], 'htmltemplate', 'params', warningOptions);
    replyWarnings = getSignatureWarnings(queryResult, '', [], 'replyhtmltemplate', 'params', warningOptions);
    primaryTextWarnings = getSignatureWarnings(queryResult, '', [], 'texttemplate', 'params', warningOptions);
    replyTextWarnings = getSignatureWarnings(queryResult, '', [], 'replytexttemplate', 'params', warningOptions);

    queryResult = { ...queryResult };
  };

  const onSave = async () => {
    errorMessage = '';

    updating = true;

    if (create) {
      const response = await TagBlocksService.create(queryResult);

      updating = false;

      if (response.status >= 400) {
        return onError({
          ...response,
          text: $format('label.ERROR_SIGNATURE_CREATION')
        });
      }

      addNotification({
        text: $format('label.SUCCESS.SIGNATURE_CREATION', { values: { name: response.rulename } }),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });

      window.location.href = `${basepath}/signatures/studio/${response.rulename}`;

      return;
    }

    const response = await TagBlocksService.save(queryResult);

    updating = false;

    if (response.status >= 200) {
      return onError({
        ...response,
        text: $format('label.FAILURE.UPDATE_SIGNATURE', { values: { rulename: queryResult.rulename } })
      });
    }

    if (originalResult.rulename !== queryResult.rulename) {
      window.location.href = `${basepath}/signatures/studio/${queryResult.rulename}`;
    }

    await updateSignatureConfig();

    showSignatureSource = false;
    showReplySignatureSource = false;
    showTextSignatureSource = false;

    addNotification({
      text: $format('label.SUCCESS.UPDATE', { values: { item: queryResult.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onClone = async () => {
    updating = true;

    const rulename = generateUniqueRulename(queryResult.rulename);

    const data = {
      rulename,
      htmltemplate: queryResult.htmltemplate,
      texttemplate: queryResult.texttemplate,
      replyhtmltemplate: queryResult.replyhtmltemplate,
      replytexttemplate: queryResult.replytexttemplate,
      replytemplateenabled: queryResult.replytemplateenabled,
      params: queryResult.params
    };

    const response = await TagBlocksService.create(data);

    updating = false;

    if (response.status >= 200) {
      return onError({
        ...response,
        notifyOnly: true,
        text: $format('label.FAILURE.CLONE_SIGNATURE', { values: { rulename: queryResult.rulename } })
      });
    }

    addNotification({
      text: $format('label.SUCCESS.CLONE', { values: { type: queryResult.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    window.location.href = `${basepath}/signatures/studio/${rulename}`;
  };

  const onDelete = async () => {
    errorMessage = '';

    updating = true;

    const response = await TagBlocksService.delete(queryResult);

    updating = false;

    if (response.status === 500) {
      return onError({
        ...response,
        notifyOnly: true,
        text: $format('label.FAILURE.DELETE', { values: { type: queryResult.rulename } })
      });
    }

    addNotification({
      text: $format('label.SUCCESS.DELETE', { values: { type: queryResult.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    redirect(`${basepath}/signatures`);
  };

  const onArchive = async () => {
    updating = true;

    const response = await TagBlocksService.save({ ...queryResult, archived: !queryResult.archived });

    if (response.status >= 400) {
      updating = false;
      return onError($format('label.FAILURE.ARCHIVE_SIGNATURE', { values: { rulename: queryResult.rulename } }));
    }

    addNotification({
      text: $format('label.SUCCESS.ARCHIVE', { values: { type: queryResult.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    queryResult = { ...queryResult, archived: response.archived };

    updating = false;
  };

  const toggleEditorPane = () => {
    showEditorPane = !showEditorPane;
  };

  const usernameOf = (email) =>
    String(email || '')
      .split('@')
      .shift()
      .toLowerCase();

  const findLoadedUser = (email) => {
    const username = usernameOf(email);

    return (username && users?.find((user) => usernameOf(user.value) === username)) || null;
  };

  // Mirrors the Business Cards designer: resolve a real user from the loaded list and fall back to
  // `isManaging ? users[0] : currentUser`. Preferring a routed sender is signature-specific -- the
  // preview is most useful as someone the signature actually applies to.
  const selectSender = async (usersLookup) => {
    if (!users.length) {
      await getUsers();
    }

    const routedSenders = senderGroups
      .flatMap(({ senders }) => senders.map(({ username }) => username))
      .filter((username) => username && username !== '*');

    const currentUser = findLoadedUser($userStore.currentUser.email);
    const myUsername = usernameOf($userStore.currentUser.email);
    const routedToMe = Boolean(currentUser) && routedSenders.some((sender) => usernameOf(sender) === myUsername);

    let resolved = routedToMe ? currentUser : routedSenders.map(findLoadedUser).find(Boolean);

    // A routed sender outside the first page of users; the search appends them to the list.
    if (!resolved && routedSenders.length && usersLookup) {
      await previewSettingsComponent?.onUserSearch(routedSenders[0]);
      resolved = findLoadedUser(routedSenders[0]);
    }

    resolved = resolved || ($userStore.isManaging ? users[0] : currentUser || users[0]);

    if (resolved) {
      selectedUser = { ...resolved };
    }
  };

  let senderGroups = [];

  const getSignatureGroups = async (signatures) => {
    const groups = await GroupsService.getAll();
    const blocks = signatures.flatMap(({ sendergroups }) => sendergroups).map(({ groupname }) => groupname);
    const matches = groups.filter(
      ({ groupname }) =>
        groups.filter(({ groupname }) => blocks.includes(groupname)).filter((group) => groupname === group.groupname)
          .length
    );

    senderGroups = matches;

    groupOptions = senderGroups.map(({ groupname }) => ({
      label: groupname,
      value: groupname,
      checked: senderGroups.includes(groupname)
    }));

    await selectSender(true);
  };

  const processUsers = (data) => {
    return data.map((user) => {
      const username = user.email.split('@').shift();
      const meta = userMetadata({ ...user, features: $userStore.features });

      return {
        ...meta,
        username,
        label: meta.name,
        value: user.email
      };
    });
  };

  const getUsers = async () => {
    let response = await UsersService.getUsers({ ...USER_REQUEST_PARAMS, filterByDate: false, perPage: 100 });

    users = originalUsers = processUsers(response?.data || []).sort((a, b) => a.label - b.label);
  };

  const getDomains = async () => {
    const { data } = await DomainsService.getAll({ screen: $userStore.isStaff ? 'staff' : '', perPage: 5000 });
    domains = data.map(({ domainname }) => ({ value: domainname, label: domainname }));
  };

  onMount(async () => {
    if (create) {
      const raw = $params.template;
      const t = raw === 'blank' ? null : Number.isInteger(Number(raw)) ? { id: Number(raw) } : null;

      queryResult = buildDraftSignature(t);
      queryResult.currentDomain = $userStore.domain;

      updateSignatureConfig();

      hasRouting = false;

      await selectSender(true);

      loaded = true;

      getDomains();

      return;
    }

    queryResult = await TagBlocksService.getById($params.id);

    if (!queryResult) {
      navigate('/signatures/studio');
      return;
    }

    queryResult.currentDomain = $userStore.domain;

    updateSignatureConfig();

    const signatures = await SignaturesService.getAll();
    const matches = signatures.filter(({ blockname }) => blockname === queryResult.rulename);

    await getSignatureGroups(matches);

    loaded = true;

    hasRouting = matches?.length && matches?.filter((match) => match.status).length;

    const warningOptions = { isOutlookVisualPreview: $isOutlookVisualPreview };

    primaryWarnings = getSignatureWarnings(queryResult, '', [], 'htmltemplate', 'params', warningOptions);
    replyWarnings = getSignatureWarnings(queryResult, '', [], 'replyhtmltemplate', 'params', warningOptions);

    getDomains();
  });

  $: fieldsModified = JSON.stringify(originalResult.params) !== JSON.stringify(fields);

  $: if (
    $appStore.mobile &&
    offcanvasElement &&
    rightPaneElement &&
    currentElement !== offcanvasElement &&
    !offcanvasElement.children.length
  ) {
    currentElement = offcanvasElement;
    offcanvasElement.appendChild(rightPaneElement.children[0]);
  }

  $: if (
    !$appStore.mobile &&
    offcanvasElement &&
    rightPaneElement &&
    currentElement !== rightPaneElement &&
    offcanvasElement.children.length
  ) {
    currentElement = rightPaneElement;
    rightPaneElement.appendChild(offcanvasElement.children[0]);
  }

  $: hasChanges = fieldsModified || JSON.stringify(originalResult) !== JSON.stringify(queryResult);
  $: isSalesforce = $userStore.domain === 'salesforce.com';
</script>

<div id="signature-editor" class="page signature-editor">
  <Loader show={!loaded || updating} backdrop={updating} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/signatures" use:link>{$format('label.SIGNATURES')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/signatures/studio" use:link>{$format('label.DESIGNER_STUDIO')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>
              {$format(create ? 'label.NEW_SIGNATURE' : 'label.EDIT_SIGNATURE')}
            </BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center" style="max-width: 1248px;">
        <Col xs="8" sm="6" md="6">
          <h1 class="page-title">
            {signatureName}

            {#if hasRouting && !$appStore.mobile}
              <div class="status">{$format('label.ROUTING')}</div>
            {/if}
            {#if queryResult?.archived && !$appStore.mobile}
              <div class="status archived">{$format('label.ARCHIVED')}</div>
            {/if}

            {#if queryResult}
              <div class="warnings" on:click|preventDefault|stopPropagation role="button" tabindex="0">
                {#each getAllSignatureWarnings( queryResult, $userStore?.cname[1], [$userStore.domain], [SIGNATURE_WARNINGS.missing_params.id, SIGNATURE_WARNINGS.unused_params.id], { isOutlookVisualPreview: $isOutlookVisualPreview } ) as { type, data }}
                  <div
                    class="warning"
                    id="warnings-{type}-{queryResult._id}"
                    style="border-color: {SIGNATURE_WARNINGS[type]?.color}; color: {SIGNATURE_WARNINGS[type]?.color};"
                  >
                    <Icon name={SIGNATURE_WARNINGS[type]?.icon} size={SIGNATURE_WARNINGS[type]?.iconSize} />
                  </div>
                  <Tooltip class="warnings-tooltip" target="warnings-{type}-{queryResult._id}">
                    <div class="warning-tooltip-content">
                      <b>{$format(SIGNATURE_WARNINGS[type]?.label)} ({data.length})</b>
                      <ul>
                        {#each data.slice(0, MAX_TOOLTIP_ITEMS) as item}
                          <li>{item}</li>
                        {/each}
                        {#if data.length > MAX_TOOLTIP_ITEMS}
                          <li>+ {data.length - MAX_TOOLTIP_ITEMS} {$format('label.MORE')}</li>
                        {/if}
                      </ul>
                    </div>
                  </Tooltip>
                {/each}
              </div>
            {/if}
          </h1>
        </Col>
        <Col xs="4" sm="6" md="6" class="text-right" style="padding: 0;">
          {#if !create}
            <ButtonDropdown class="hide--mobile">
              <DropdownToggle data-testid="actions-toggle" color="secondary" caret>
                {$format('label.ACTIONS')}
              </DropdownToggle>
              <DropdownMenu>
                {#if !hasRouting}
                  <DropdownItem data-testid="sig-action-archive-{queryResult?.rulename}" on:click={onArchive}>
                    {#if !queryResult?.archived}
                      {$format('label.ARCHIVE')}
                    {:else}
                      {$format('label.UNARCHIVE')}
                    {/if}
                  </DropdownItem>
                {/if}
                <DropdownItem on:click={onClone}>{$format('label.CLONE')}</DropdownItem>
                {#if $userStore.isStaff}
                  <DropdownItem divider />
                  <DropdownItem on:click={onCloneSignatureToDomain}>{$format('label.CLONE_TO_DOMAIN')}</DropdownItem>
                {/if}
                {#if !hasRouting}
                  <DropdownItem divider />
                  <DropdownItem data-testid="delete-sig" on:click={onDelete}>
                    {$format('label.DELETE')}
                  </DropdownItem>
                {/if}
              </DropdownMenu>
            </ButtonDropdown>

            <div class="action-divider hide--mobile"></div>
          {/if}

          <Button color="secondary" class="hide--mobile me-2" on:click={() => redirect('/signatures/studio')}>
            {$format('label.CANCEL')}
          </Button>

          <Button
            data-testid="update-btn"
            color="primary"
            disabled={updating || (!create && !hasChanges)}
            on:click={onSave}
          >
            {$format(create ? 'label.SAVE' : 'label.UPDATE')}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if loaded}
      <div class="editor-layout">
        <div class="editor-left-pane">
          {#if $appStore.mobile}
            <div class="mobile-actions">
              {#if hasRouting && $appStore.mobile}
                <div class="status">{$format('label.ROUTING')}</div>
              {/if}
              <Button data-testid="edit-settings-btn" color="secondary" size="sm" on:click={toggleEditorPane}>
                {$format('label.EDIT_SETTINGS')}
              </Button>
            </div>
          {/if}

          {#if errorMessage.length}
            <Alert color="danger" dismissible>
              <h4 class="alert-heading text-capitalize">{$format('label.ERROR')}</h4>
              {errorMessage}
            </Alert>
          {/if}

          <h2>
            {$format('label.SIGNATURE')}
            {#each primaryWarnings as { type, data }}
              {#if type === SIGNATURE_WARNINGS.html_lint_errors.id || type === SIGNATURE_WARNINGS.third_party_urls.id}
                <div class="icons">
                  <span id="warnings-{type}-fields-tooltip">
                    <Icon name={SIGNATURE_WARNINGS[type]?.icon} size={SIGNATURE_WARNINGS[type]?.iconSize} />
                    {$format(SIGNATURE_WARNINGS[type]?.label)}
                  </span>
                  <Tooltip target="warnings-{type}-fields-tooltip">
                    <div class="warning-tooltip-content">
                      <b>{$format(SIGNATURE_WARNINGS[type]?.label)} ({data.length})</b>
                      <ul>
                        {#each data.slice(0, MAX_TOOLTIP_ITEMS) as item}
                          <li>{item}</li>
                        {/each}
                        {#if data.length > MAX_TOOLTIP_ITEMS}
                          <li>+ {data.length - MAX_TOOLTIP_ITEMS} {$format('label.MORE')}</li>
                        {/if}
                      </ul>
                    </div>
                  </Tooltip>
                </div>
              {/if}
            {/each}
          </h2>

          <SignatureEditor
            bind:data={queryResult}
            bind:source={queryResult.htmltemplate}
            bind:fields={queryResult.params}
            bind:selectedUser
            bind:recipientEmail
            bind:actions
            bind:showSource={showSignatureSource}
            originalSource={originalResult.htmltemplate}
          />

          {#if activeFields.length}
            <div class="active-signature-fields">
              <b>
                {$format('label.FIELDS')}
                <Icon name="help-circle" id="signature-fields-tooltip" />
                <Tooltip target="signature-fields-tooltip">
                  {$format('label.TOOLTIP.FIELDS_SIGNATURE')}
                </Tooltip>
              </b>
              <div class="active-fields">
                {#each activeFields as field, index}
                  <div class="active-field" data-testid="active-field-{field}">{field}</div>
                {/each}
              </div>
            </div>
          {/if}

          {#if activeInternalFields.length}
            <div class="active-signature-fields">
              <b>
                {$format('label.INTERNAL_FIELDS')}
                <Icon name="help-circle" id="signature-internal-fields-tooltip" />
                <Tooltip target="signature-internal-fields-tooltip">
                  {$format('label.TOOLTIP.INTERNAL_FIELDS')}
                </Tooltip>
              </b>
              <div class="active-fields">
                {#each activeInternalFields as field, index}
                  <div class="active-field internal">{field}</div>
                {/each}
              </div>
            </div>
          {/if}

          <SignatureParamWarnings
            warnings={primaryWarnings}
            context="primary"
            template="htmltemplate"
            onAddToFields={addToSignatureFields}
            onFixInlineImages={fixInlineImages}
          />

          {#if Object.keys(activeSpecialFields).length}
            {#each Object.entries(activeSpecialFields) as [type, fields]}
              <div class="active-signature-fields">
                <b>
                  {$format(FIELD_TYPES.SPECIAL[type]?.label) || type}
                  <Icon name="help-circle" id="{type}-special-fields-tooltip" />
                  <Tooltip target="{type}-special-fields-tooltip">
                    {$format(FIELD_TYPES.SPECIAL[type]?.tooltip)}
                  </Tooltip>
                </b>
                <div class="active-fields">
                  {#each fields as field}
                    {#if type === 'plugin'}
                      <a href="{basepath}/manage/integrations/{field}" class="active-field plugin" use:link>{field}</a>
                    {:else if type === 'adtag'}
                      <a href="{basepath}/marketing/campaigns/{field}" class="active-field ad" use:link>{field}</a>
                    {:else}
                      <div class="active-field special {type}">{field}</div>
                    {/if}
                  {/each}
                </div>
              </div>
            {/each}
          {/if}

          {#if queryResult.replytemplateenabled}
            <h2>
              {$format('label.REPLY_SIGNATURE')}
              {#each replyWarnings as { type, data }}
                {#if (type === SIGNATURE_WARNINGS.html_lint_errors.id || type === SIGNATURE_WARNINGS.third_party_urls.id) && data.length}
                  <div class="icons">
                    <span id="warnings-{type}-reply-fields-tooltip">
                      <Icon name={SIGNATURE_WARNINGS[type]?.icon} size={SIGNATURE_WARNINGS[type]?.iconSize} />
                      {$format(SIGNATURE_WARNINGS[type]?.label)}
                    </span>
                    <Tooltip target="warnings-{type}-reply-fields-tooltip">
                      <div class="warning-tooltip-content">
                        <b>{$format(SIGNATURE_WARNINGS[type]?.label)} ({data.length})</b>
                        <ul>
                          {#each data.slice(0, MAX_TOOLTIP_ITEMS) as item}
                            <li>{item}</li>
                          {/each}
                          {#if data.length > MAX_TOOLTIP_ITEMS}
                            <li>+ {data.length - MAX_TOOLTIP_ITEMS} {$format('label.MORE')}</li>
                          {/if}
                        </ul>
                      </div>
                    </Tooltip>
                  </div>
                {/if}
              {/each}
            </h2>

            <SignatureEditor
              bind:data={queryResult}
              bind:source={queryResult.replyhtmltemplate}
              bind:fields={queryResult.params}
              bind:selectedUser
              bind:recipientEmail
              bind:showSource={showReplySignatureSource}
              originalSource={originalResult.replyhtmltemplate}
            />

            {#if activeReplyFields.length}
              <div class="active-signature-fields">
                <b>
                  {$format('label.FIELDS')}
                  <Icon name="help-circle" id="reply-fields-tooltip" />

                  <Tooltip target="reply-fields-tooltip">
                    {$format('label.TOOLTIP.FIELDS_SIGNATURE')}
                  </Tooltip>
                </b>
                <div class="active-fields">
                  {#each activeReplyFields as field, index}
                    <div class="active-field">{field}</div>
                  {/each}
                </div>
              </div>
            {/if}

            {#if activeReplyInternalFields.length}
              <div class="active-signature-fields">
                <b>
                  {$format('label.INTERNAL_FIELDS')}
                  <Icon name="help-circle" id="reply-internal-fields-tooltip" />
                  <Tooltip target="reply-internal-fields-tooltip">
                    {$format('label.TOOLTIP.INTERNAL_FIELDS')}
                  </Tooltip>
                </b>
                <div class="active-fields internal">
                  {#each activeReplyInternalFields as field, index}
                    <div class="active-field internal">{field}</div>
                  {/each}
                </div>
              </div>
            {/if}

            <SignatureParamWarnings
              warnings={replyWarnings}
              context="reply"
              template="replyhtmltemplate"
              onAddToFields={addToSignatureFields}
              onFixInlineImages={fixInlineImages}
            />
          {/if}

          <h2>{$format('label.TEXT_SIGNATURE')}</h2>

          <SignatureEditor
            bind:data={queryResult}
            bind:source={queryResult.texttemplate}
            bind:fields={queryResult.params}
            bind:selectedUser
            bind:recipientEmail
            bind:showSource={showTextSignatureSource}
            originalSource={originalResult.texttemplate}
            editLabel={$format('label.EDIT_TEXT')}
            textOnly
          />

          {#if activeTextFields.length}
            <div class="active-signature-fields">
              <b>
                {$format('label.FIELDS')}
                <Icon name="help-circle" id="text-fields-tooltip" />

                <Tooltip target="text-fields-tooltip">
                  {$format('label.TOOLTIP.FIELDS_SIGNATURE')}
                </Tooltip>
              </b>
              <div class="active-fields">
                {#each activeTextFields as field, index}
                  <div class="active-field">{field}</div>
                {/each}
              </div>
            </div>
          {/if}

          {#each primaryTextWarnings as { type, data }}
            {#if (type === SIGNATURE_WARNINGS.missing_params.id || type === SIGNATURE_WARNINGS.unused_params.id) && data.length}
              <div class="active-signature-fields">
                <b
                  >{$format(SIGNATURE_WARNINGS[type]?.label)}
                  <Icon name="help-circle" id="{type}-text-signature-fields" />
                  <Tooltip target="{type}-text-signature-fields">
                    {$format(SIGNATURE_WARNINGS[type].tooltip)}
                  </Tooltip>
                </b>
                <div class="active-fields">
                  {#each data as item}
                    <div
                      class="active-field"
                      data-testid="active-field-{item}"
                      style="background-color: {SIGNATURE_WARNINGS[type].color}; color: #fff;"
                    >
                      {item}

                      {#if type === SIGNATURE_WARNINGS.missing_params.id}
                        <Icon
                          name="plus"
                          class="pointer"
                          style="padding-left: var(--padding-base); border-left: 1px solid rgba(255,255,255,0.25)"
                          on:click={() => addToSignatureFields(item, type)}
                          role="button"
                          tabindex="0"
                        />
                      {/if}
                    </div>
                  {/each}
                </div>
              </div>
            {/if}
          {/each}

          {#if queryResult.replytemplateenabled}
            <h2>{$format('label.REPLY_TEXT_SIGNATURE')}</h2>
            <SignatureEditor
              bind:data={queryResult}
              bind:source={queryResult.replytexttemplate}
              bind:fields={queryResult.params}
              bind:selectedUser
              bind:recipientEmail
              originalSource={originalResult.replytexttemplate}
              editLabel={$format('label.EDIT_TEXT')}
              textOnly
            />

            {#if activeReplyTextFields.length}
              <div class="active-signature-fields">
                <b>
                  {$format('label.FIELDS')}
                  <Icon name="help-circle" id="text-reply-fields-tooltip" />

                  <Tooltip target="text-reply-fields-tooltip">
                    {$format('label.TOOLTIP.FIELDS_SIGNATURE')}
                  </Tooltip>
                </b>
                <div class="active-fields">
                  {#each activeReplyTextFields as field, index}
                    <div class="active-field">{field}</div>
                  {/each}
                </div>
              </div>
            {/if}

            {#each replyTextWarnings as { type, data }}
              {#if (type === SIGNATURE_WARNINGS.missing_params.id || type === SIGNATURE_WARNINGS.unused_params.id) && data.length}
                <div class="active-signature-fields">
                  <b
                    >{$format(SIGNATURE_WARNINGS[type]?.label)}
                    <Icon name="help-circle" id="{type}-reply-text-signature-fields" />
                    <Tooltip target="{type}-reply-text-signature-fields">
                      {$format(SIGNATURE_WARNINGS[type].tooltip)}
                    </Tooltip>
                  </b>
                  <div class="active-fields">
                    {#each data as item}
                      <div
                        class="active-field"
                        style="background-color: {SIGNATURE_WARNINGS[type].color}; color: #fff;"
                      >
                        {item}

                        {#if type === SIGNATURE_WARNINGS.missing_params.id}
                          <Icon
                            name="plus"
                            class="pointer"
                            style="padding-left: var(--padding-base); border-left: 1px solid rgba(255,255,255,0.25)"
                            on:click={() => addToSignatureFields(item, type)}
                            role="button"
                            tabindex="0"
                          />
                        {/if}
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
            {/each}
          {/if}
        </div>
        <div class="editor-right-pane" bind:this={rightPaneElement}>
          <div class="signature-settings">
            <h2>{$format('label.PREVIEW')}</h2>
            <SignaturePreviewSettings
              bind:this={previewSettingsComponent}
              bind:users
              bind:originalUsers
              bind:selectedUser
              bind:recipientEmail
              bind:actions
            />

            <h2>{$format('label.SETTINGS')}</h2>

            <TextField
              id="rulename"
              label={$format('label.NAME')}
              placeholder={$format('label.PLACEHOLDER.NAME_SIGNATURE')}
              tooltip={$format('label.TOOLTIP.NAME_SIGNATURE')}
              bind:value={queryResult.rulename}
              editing={true}
              showActions={false}
              formatInput={sanitizeInputValue}
              onChange={onUpdate}
            />

            <MultiSelectField
              id="groups"
              label={$format('label.GROUPS')}
              placeholder={$format('label.NO_GROUPS_ASSIGNED')}
              tooltip={$format('label.TOOLTIP.GROUPS')}
              bind:selected={groupOptions}
              bind:options={groupOptions}
              editing={false}
              showActions={false}
              {onUpdate}
            >
              <div class="groups">
                {#if groupOptions.length}
                  {#each groupOptions as group}
                    <div class="group">{group.label}</div>
                  {/each}
                {:else}
                  {$format('label.NO_GROUPS_ASSIGNED')}
                {/if}
              </div>
            </MultiSelectField>

            <SwitchField
              id="replytemplateenabled"
              label={$format('label.REPLY_SIGNATURE')}
              tooltip={$format('label.TOOLTIP.REPLY_SIGNATURE')}
              valueOn={$format('label.ENABLED')}
              valueOff={$format('label.DISABLED')}
              editing={true}
              showActions={false}
              bind:checked={queryResult.replytemplateenabled}
              onChange={onUpdate}
            />

            <h2>
              <span class="fields-heading">
                {$format('label.FIELDS')}
                <Icon name="help-circle" id="fields-tooltip" />
              </span>
              <Tooltip target="fields-tooltip">
                {@html $format('label.TOOLTIP.FIELDS')}
                {#if isSalesforce}
                  {@html $format('label.TOOLTIP.FIELDS_SALESFORCE')}
                {/if}
              </Tooltip>

              {#if fieldsModified}
                <span class="modified">{$format('label.MODIFIED')}</span>
                <span class="btn btn-secondary btn-sm" on:click={onResetFields} role="button" tabindex="0"
                  >{$format('label.RESET')}</span
                >
              {/if}
            </h2>

            <SignatureFieldsList
              bind:fields
              {isSalesforce}
              {activeFields}
              {activeTextFields}
              {activeReplyFields}
              {activeReplyTextFields}
              on:change={onFieldsChange}
            />
          </div>
        </div>
      </div>
    {/if}
  </div>

  <Offcanvas isOpen={showEditorPane} toggle={toggleEditorPane} placement="end" style="width: 90%;">
    <div slot="header" class="offcanvas-heading">
      <Icon name="settings" />
      <span>{$format('label.SIGNATURE_SETTINGS')}</span>
    </div>
    <div bind:this={offcanvasElement} />
  </Offcanvas>
</div>

<CloneModal
  bind:show={showCloneToDomainModal}
  bind:signature={cloneSignature}
  bind:rulename={cloneSignatureRulename}
  {domains}
  bind:updating
  domain={$userStore.domain}
/>

<style lang="scss" src="./edit.scss"></style>
